/**
 * API bilan complet 360° d'un étudiant.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface BilanIdentite {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  sexe: string;
  date_naissance: string | null;
  lieu_naissance: string;
  niveau: string;
  classe: string;
  option: string;
  parcours: string;
  pole: string;
  type_formation: string;
  email: string;
  telephone: string;
  actif: boolean;
  photo_url: string | null;
  date_import: string | null;
}

export interface Alerte {
  niveau: 'danger' | 'warning' | 'info';
  code: string;
  titre: string;
  action: string;
}

export interface BilanResponse {
  identite: BilanIdentite;
  alertes: Alerte[];
  resultats: {
    semestres?: Array<{
      annee: string; semestre: string;
      moyenne: number | null; credits_valides: number; credits_totaux: number;
      decision: string; valide: boolean; mention: string; doit_rattraper: boolean;
      rang: number | null; effectif_classe: number | null;
      session_validation: string;
    }>;
    ue_derniere?: Array<{
      ue_code: string; ue_libelle: string;
      moyenne: number | null; credits: number;
      valide: boolean; eliminatoire: boolean; session_validation: string;
    }>;
    top_matieres?: Array<{ nom: string; code: string; moyenne: number | null }>;
    matieres_a_ameliorer?: Array<{ nom: string; code: string; moyenne: number | null }>;
  };
  assiduite: {
    taux_presence: number | null;
    stats?: {
      total_pointages: number; presents: number; absents: number;
      retards: number; justifies: number; incidents: number;
    };
    par_matiere?: Array<{
      matiere: string; total: number; absents: number; retards: number; taux_present: number;
    }>;
    absences_recentes?: Array<{
      date: string; heure: string; matiere: string; classe: string;
      statut: string; justifie: boolean;
      justification_par_parent: boolean; motif: string;
    }>;
    retards_recents?: Array<{
      date: string; matiere: string; minutes: number;
    }>;
  };
  conduite: {
    entrees?: Array<{
      id: number; date_observation: string | null;
      type: 'positif' | 'negatif'; categorie: string; categorie_display: string;
      poids: number; poids_display: string;
      commentaire: string; auteur_nom: string; auteur_role: string;
    }>;
    stats?: {
      positifs?: number; negatifs?: number; score?: number;
      par_categorie?: Record<string, number>;
    };
  };
  sante: {
    fiche: {
      groupe_sanguin: string; allergies: string;
      traitements_en_cours: string; antecedents: string; observations: string;
    } | null;
    contacts_urgence?: Array<{ nom: string; lien: string; telephone: string }>;
    incidents_recents?: Array<{
      date: string | null; type: string; description: string; gravite: string;
    }>;
  } | null;
  fraudes: Array<{
    id: number; reference: string; type_fraude: string;
    gravite: string; statut: string; statut_code: string;
    sanction: string; date: string | null;
    matiere_nom: string; matiere_code: string;
  }>;
  reclamations: Array<{
    id: number;
    type_reclamation: string; type_code: string;
    statut: string; statut_code: string;
    matiere_nom: string; semestre: string;
    note_actuelle: number | null; note_attendue: number | null;
    motif: string; reponse_scolarite: string;
    traite_par_nom: string;
    date_creation: string | null; date_traitement: string | null;
    derniere_activite: string | null;
    nb_messages: number; nb_non_lus_admin: number;
  }>;
  communication: {
    messages_parents?: Array<{
      id: number; date_envoi: string | null; sujet: string;
      contenu: string; destinataire: string;
      auteur_type: string; auteur_nom: string; lu: boolean;
    }>;
    messages_etudiant?: Array<{
      id: number; date: string | null; sujet: string;
      message: string; reponse: string; lu: boolean; traite: boolean;
      statut: string; repondu_par_nom: string; important: boolean;
    }>;
  };
  rachats: Array<{
    id: number; matiere: string; matiere_code: string;
    note_avant: number | null; note_apres: number | null;
    type_operation: string; type_code: string; annulation: boolean;
    source_note: string; justification: string;
    date: string | null;
  }>;
  rattrapages: Array<{
    id: number; matiere: string; choix?: string;
    date_choix: string | null; annee: string; semestre: string;
  }>;
  paiement: {
    annee_academique: string; inscription: boolean; mar_paye: boolean;
    exam_paye: boolean; seuil_a_jour: boolean; derniere_maj: string | null;
  } | null;
  generated_at: string;
}

export const bilanEtudiantApi = createApi({
  reducerPath: 'bilanEtudiantApi',
  baseQuery: baseQueryWithHeaders,
  endpoints: (b) => ({
    getBilan: b.query<BilanResponse, string>({
      query: (matricule) => `/etudiants/${matricule.toUpperCase()}/bilan/`,
    }),
  }),
});

export const { useGetBilanQuery } = bilanEtudiantApi;
